import React, { useRef, useMemo } from 'react';
import { ChevronUpIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AvailabilityReportDetail, AvailabilityReportItemRow } from '@/types';
import { LocalEdit, NewItem } from '@/stores/availability-report/availability-report-store';
import { clsx } from 'clsx';
import { formatLastChange } from '@/utils/report-format';
import { highlightText } from '@/utils/inventory/search-highlight';
import { ReportTableSkeleton } from './ReportTableSkeleton';
import { VirtualizedGrid } from '@/components/shared/VirtualizedGrid';

const COLUMN_WIDTHS = {
  rowNumber: 40,
  symbol_id: 220,
  counterparty_product_id: 220,
  quantity: 160,
  lastChange: 280,
} as const;

type SortState = { id: string; desc: boolean } | null;

// Virtual row for new (unsaved) items carries the stable tempId so edits target
// the right entry even if product_id is duplicated or emptied mid-edit.
type VirtualRow = AvailabilityReportItemRow & { _tempId?: string };

interface AvailabilityReportTableProps {
  report: AvailabilityReportDetail | undefined;
  isLoading: boolean;
  error: unknown;
  sorting: SortState;
  onSortChange: (sorting: SortState) => void;
  isEditing: boolean;
  localEdits: Record<number, LocalEdit>;
  newItems: NewItem[];
  isMutating: boolean;
  onUpdateItemQuantity: (id: number, quantity: number, originalQuantity: number) => void;
  onUpdateItemCounterpartyProductId: (id: number, value: string, originalValue: string) => void;
  onToggleItemDeleted: (id: number) => void;
  onRemoveNewItem: (tempId: string) => void;
  onUpdateNewItemQuantity: (tempId: string, quantity: number) => void;
  onUpdateNewItemCounterpartyProductId: (tempId: string, counterpartyProductId: string) => void;
  searchQuery?: string;
}

const AvailSortButton: React.FC<{
  columnId: string;
  label: string;
  width: number;
  showCsvIndicator?: boolean;
  sorting: SortState;
  onToggleSort: (columnId: string) => void;
  disabled: boolean;
}> = ({ columnId, label, width, showCsvIndicator, sorting, onToggleSort, disabled }) => {
  const isActive = sorting !== null && sorting.id === columnId;
  const isDesc = isActive && sorting!.desc;
  const isAsc = isActive && !sorting!.desc;
  return (
    <button
      onClick={() => onToggleSort(columnId)}
      disabled={disabled}
      className="mx-1.5 py-1 flex items-center transition-colors disabled:cursor-not-allowed"
      style={{ width: `${width}px`, minWidth: `${width}px` }}
    >
      <span className="mr-1">{label}</span>
      {showCsvIndicator && (
        <span title="This column is included in the CSV export">
          <svg className="h-3.5 w-3.5 inline-block ml-0.5" viewBox="0 0 16 16" fill="none">
            <path d="M3 1h7l3 3v11H3V1z" fill="#34A853" />
            <path d="M10 1v3h3" fill="#1E8E3E" />
            <rect x="5" y="6" width="6" height="1" rx=".5" fill="#fff" />
            <rect x="5" y="8.5" width="6" height="1" rx=".5" fill="#fff" />
            <rect x="5" y="11" width="6" height="1" rx=".5" fill="#fff" />
            <line x1="8" y1="6" x2="8" y2="12" stroke="#fff" strokeWidth=".8" />
          </svg>
        </span>
      )}
      {isDesc && <ChevronDownIcon className="h-3 w-3 text-gray-400" />}
      {isAsc && <ChevronUpIcon className="h-3 w-3 text-gray-400" />}
      {!isActive && <span className="h-3 w-3" />}
    </button>
  );
};

export const AvailabilityReportTable: React.FC<AvailabilityReportTableProps> = ({
  report,
  isLoading,
  error,
  sorting,
  onSortChange,
  isEditing,
  localEdits,
  newItems,
  isMutating,
  onUpdateItemQuantity,
  onUpdateItemCounterpartyProductId,
  onToggleItemDeleted,
  onRemoveNewItem,
  onUpdateNewItemQuantity,
  onUpdateNewItemCounterpartyProductId,
  searchQuery,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);

  // In edit mode, show all items (including server-deleted) plus new items; in read-only, filter deleted
  const visibleItems = useMemo(() => {
    const existing = report?.items ?? [];
    let items: VirtualRow[];
    if (!isEditing || newItems.length === 0) {
      items = existing;
    } else {
      const added: VirtualRow[] = newItems.map((ni, i) => ({
        id: -(i + 1),
        _tempId: ni.tempId,
        symbol_id: ni.symbol_id,
        counterparty_product_id: ni.counterparty_product_id,
        original_counterparty_product_id: ni.counterparty_product_id,
        original_quantity: 0,
        quantity: ni.quantity,
        is_modified: false,
        is_deleted: false,
        is_manually_added: true,
        modified_by_email: null,
        modified_at: null,
      }));
      items = [...existing, ...added];
    }
    if (searchQuery?.trim()) {
      const q = searchQuery.trim().toLowerCase();
      items = items.filter(
        (item) =>
          item.symbol_id.toLowerCase().includes(q) ||
          item.counterparty_product_id.toLowerCase().includes(q) ||
          String(item.quantity).includes(q),
      );
    }
    return items;
  }, [isEditing, report?.items, newItems, searchQuery]);

  const sortedItems = useMemo(() => {
    if (!sorting && isEditing) {
      return [...visibleItems].sort((a, b) =>
        a.symbol_id.toLowerCase().localeCompare(b.symbol_id.toLowerCase())
      );
    }
    const items = [...visibleItems];
    items.sort((a, b) => {
      if (sorting) {
        const aVal = a[sorting.id as keyof AvailabilityReportItemRow];
        const bVal = b[sorting.id as keyof AvailabilityReportItemRow];
        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sorting.desc ? bVal - aVal : aVal - bVal;
        }
        const sa = String(aVal).toLowerCase();
        const sb = String(bVal).toLowerCase();
        if (sa < sb) return sorting.desc ? 1 : -1;
        if (sa > sb) return sorting.desc ? -1 : 1;
      }
      return 0;
    });
    return items;
  }, [visibleItems, sorting, isEditing]);

  const toggleSort = (columnId: string) => {
    if (!sorting || sorting.id !== columnId) {
      onSortChange({ id: columnId, desc: false });
    } else if (!sorting.desc) {
      onSortChange({ id: columnId, desc: true });
    } else {
      onSortChange(null);
    }
  };

  const sortDisabled = isLoading || !!error;

  const headerNode = (
    <>
      <div
        className="mx-1.5 py-1"
        style={{ width: `${COLUMN_WIDTHS.rowNumber}px`, minWidth: `${COLUMN_WIDTHS.rowNumber}px` }}
      />
      <AvailSortButton columnId="symbol_id" label="Symbol ID" width={COLUMN_WIDTHS.symbol_id} sorting={sorting} onToggleSort={toggleSort} disabled={sortDisabled} />
      <AvailSortButton
        columnId="counterparty_product_id"
        label="Product ID"
        width={COLUMN_WIDTHS.counterparty_product_id}
        showCsvIndicator
        sorting={sorting}
        onToggleSort={toggleSort}
        disabled={sortDisabled}
      />
      <AvailSortButton columnId="quantity" label="Quantity" width={COLUMN_WIDTHS.quantity} showCsvIndicator sorting={sorting} onToggleSort={toggleSort} disabled={sortDisabled} />
      <AvailSortButton columnId="modified_at" label="Last Change" width={COLUMN_WIDTHS.lastChange} sorting={sorting} onToggleSort={toggleSort} disabled={sortDisabled} />
    </>
  );

  let bodyOverride: React.ReactNode = null;
  if (isLoading) {
    bodyOverride = <ReportTableSkeleton />;
  } else if (error) {
    bodyOverride = (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="mb-2 text-red-600">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-500">Failed to load availability report</p>
        </div>
      </div>
    );
  } else if (!report) {
    bodyOverride = (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-gray-400">Select a report from the sidebar to view its contents</p>
      </div>
    );
  } else if (sortedItems.length === 0) {
    bodyOverride = (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-gray-500">No items in this report</p>
      </div>
    );
  }

  return (
    <VirtualizedGrid<VirtualRow>
      rows={sortedItems}
      estimateRowHeight={38}
      overscan={10}
      scrollContainerRef={parentRef}
      scrollbarGutterStable={false}
      header={headerNode}
      headerClassName="px-4 py-2"
      bodyOverride={bodyOverride}
      renderRow={({ row: item, index, virtualRow, style }) => {
        const localEdit = localEdits[item.id];
        const isItemDeleted = localEdit?.isDeleted ?? false;
        const displayQuantity = localEdit?.quantity ?? item.quantity;
        const displayProductId = localEdit?.counterpartyProductId ?? item.counterparty_product_id;
        const isModified = displayQuantity !== item.original_quantity || displayProductId !== item.original_counterparty_product_id;

        const isNewItem = item.id < 0;

        const isSoftDeleted = !isEditing && item.is_deleted;
        const isDeleted = isSoftDeleted || (isEditing && isItemDeleted);

        const rowClasses = clsx(
          'flex items-center border-b border-gray-200 px-4 py-2 text-sm',
          isSoftDeleted
            ? 'opacity-50'
            : isEditing && isItemDeleted
              ? 'opacity-50'
              : isEditing && isNewItem
                ? 'bg-green-100/60 border-l-[3px] border-l-green-500'
                : isEditing && isModified
                  ? 'bg-amber-100/60 border-l-[3px] border-l-amber-500'
                  : item.is_manually_added
                    ? 'bg-green-50 border-l-[3px] border-l-green-400'
                    : 'hover:bg-gray-50',
        );

        return (
          <div key={virtualRow?.key ?? item.id} className={rowClasses} style={style}>
            <div
              style={{ width: COLUMN_WIDTHS.rowNumber, flexShrink: 0 }}
              className={clsx('mx-1.5 truncate', isDeleted && 'line-through')}
            >
              {isEditing ? (
                isNewItem ? (
                  <button
                    onClick={() => item._tempId && onRemoveNewItem(item._tempId)}
                    disabled={isMutating}
                    className="text-red-400 hover:text-red-600"
                    title="Remove"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                ) : (
                  <input
                    type="checkbox"
                    checked={!isItemDeleted}
                    onChange={() => onToggleItemDeleted(item.id)}
                    disabled={isMutating}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                )
              ) : (
                <span className="text-xs text-gray-500">{index + 1}</span>
              )}
            </div>
            <div
              style={{ width: COLUMN_WIDTHS.symbol_id, flexShrink: 0 }}
              className={clsx('mx-1.5', isDeleted && 'line-through')}
            >
              <span className={clsx(isSoftDeleted ? 'font-normal text-gray-500' : 'font-medium text-gray-900')}>{searchQuery ? highlightText(item.symbol_id, searchQuery) : item.symbol_id}</span>
            </div>
            <div
              style={{ width: COLUMN_WIDTHS.counterparty_product_id, flexShrink: 0 }}
              className={clsx('mx-1.5', isDeleted && 'line-through')}
            >
              {isEditing ? (
                <input
                  type="text"
                  value={displayProductId}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (isNewItem) {
                      if (item._tempId) onUpdateNewItemCounterpartyProductId(item._tempId, val);
                    } else {
                      onUpdateItemCounterpartyProductId(item.id, val, item.original_counterparty_product_id);
                    }
                  }}
                  disabled={isItemDeleted || isMutating}
                  className={clsx(
                    'w-full rounded border px-2 py-1 text-sm',
                    isItemDeleted
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500',
                  )}
                />
              ) : (
                <span className={isSoftDeleted ? 'text-gray-500' : 'text-gray-700'}>{searchQuery ? highlightText(item.counterparty_product_id, searchQuery) : item.counterparty_product_id}</span>
              )}
            </div>
            <div
              style={{ width: COLUMN_WIDTHS.quantity, flexShrink: 0 }}
              className={clsx('mx-1.5', isDeleted && 'line-through')}
            >
              {isEditing ? (
                <input
                  type="number"
                  min={0}
                  value={displayQuantity}
                  onChange={(e) => {
                    const val = e.target.value === '' ? 0 : Math.max(0, Number(e.target.value));
                    if (isNewItem) {
                      if (item._tempId) onUpdateNewItemQuantity(item._tempId, val);
                    } else {
                      onUpdateItemQuantity(item.id, val, item.original_quantity);
                    }
                  }}
                  disabled={isItemDeleted || isMutating}
                  className={clsx(
                    'w-full rounded border px-2 py-1 text-sm font-semibold',
                    isItemDeleted
                      ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                      : 'bg-white text-gray-900 border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500',
                  )}
                />
              ) : (
                <span className={clsx(isSoftDeleted ? 'font-normal text-gray-500' : 'font-semibold text-gray-900')}>
                  {searchQuery ? highlightText(item.quantity.toLocaleString('en-US'), searchQuery) : item.quantity.toLocaleString('en-US')}
                </span>
              )}
            </div>
            <div
              style={{ minWidth: COLUMN_WIDTHS.lastChange, flexShrink: 0 }}
              className="mx-1.5"
            >
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {formatLastChange(item)}
              </span>
            </div>
          </div>
        );
      }}
    />
  );
};
